import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle,
  Database,
  Globe,
  History,
  Mail,
  Receipt,
  RefreshCcw,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Money } from '../../components/Money';
import { Avatar } from '../../components/Avatar';
import { Tabs } from '../../components/Tabs';
import { Banner } from '../../components/Banner';
import { Modal } from '../../components/Modal';
import { Input } from '../../components/Input';
import { PosRoleSelector } from '../../components/PosRoleSelector';
import { InviteLinkResult } from '../../components/InviteLinkResult';
import { CreateOrInviteToggle, AccountCreatedPanel } from '../../components/CreateOrInvite';
import { CashPaymentModal } from '../../components/CashPaymentModal';
import { LedgerView } from '../../components/LedgerView';
import { BusinessUsersTable } from '../../components/BusinessUsersTable';
import { formatDate, formatDateOnly, dateStatus, slotOn, dhakaToday } from '../../lib/format';
import { cn } from '../../lib/cn';
import type { ResellerCustomer } from '../../mocks/reseller';
import {
  useResellerCustomers,
  useCustomerDomains,
  useCustomerAudit,
  useCustomerExtraction,
  requestCustomerExtraction,
  createResellerCustomerUser,
  type CustomerDomain,
  type CustomerAuditEvent,
  type ResellerInviteResult,
  type ResellerCreateUserResult,
  type ExtractionState,
} from '../../api/reseller';
import { useResellerCustomerLedger, type BusinessLedger } from '../../api/billing';
import { cancelCustomerSubscription } from '../../api/reseller';

const FALLBACK_CUSTOMER = {
  id: 'fallback',
  name: 'Customer',
  ownerEmail: 'owner@example.com',
  plan: 'Starter',
  status: 'active' as const,
  joinedAt: '2026-04-25',
  mrrMinor: 30000,
  unitAmountMinor: 30000,
  interval: 'month' as string | null,
  subStatus: 'active' as string | null,
  isPastDue: false,
  dbMode: 'shared',
  currentPeriodEnd: null as string | null,
  cancelAtPeriodEnd: false,
};

/** DB-mode → human label for the CustomerDetail "Database" field. */
const DB_MODE_LABEL: Record<string, string> = {
  shared: 'Managed (shared)',
  managed: 'Managed (dedicated)',
  self_hosted: 'Self-hosted',
  legacy: 'Legacy (shared)',
};
function dbModeLabel(mode?: string): string {
  return (mode && DB_MODE_LABEL[mode]) ?? 'Managed (shared)';
}

/** Status → badge tone + label. Covers BOTH the business account status enum and the
 *  subscription status enum (active/trial/past_due/expired/canceled) — the CustomerDetail
 *  badges show the subscription status, which can differ from the account status. */
const STATUS_BADGE: Record<string, { tone: 'success' | 'error' | 'warning' | 'info' | 'neutral'; label: string }> = {
  active: { tone: 'success', label: 'Active' },
  trial: { tone: 'info', label: 'Trial' },
  past_due: { tone: 'error', label: 'Past due' },
  pending_payment: { tone: 'warning', label: 'Pending payment' },
  provisioning: { tone: 'info', label: 'Provisioning' },
  suspended: { tone: 'warning', label: 'Suspended' },
  expired: { tone: 'warning', label: 'Expired' },
  canceled: { tone: 'neutral', label: 'Canceled' },
  deleted: { tone: 'neutral', label: 'Deleted' },
};
function statusBadge(status: string) {
  return STATUS_BADGE[status] ?? { tone: 'neutral' as const, label: status };
}

/** Billing interval → the price-line suffix ("Basic · ৳3,000 / year"). Falls back to
 *  'month' for an unknown/missing interval so the label is never blank. */
const INTERVAL_LABEL: Record<string, string> = {
  month: 'month',
  quarter: 'quarter',
  year: 'year',
  week: 'week',
  one_time: 'one-time',
};
function intervalLabel(interval?: string | null): string {
  return (interval && INTERVAL_LABEL[interval]) ?? 'month';
}

/** The subscription's billing status drives the CustomerDetail status badges — NOT the
 *  business account status. Falls back to the account status when there's no subscription. */
function subStatusOf(customer: Customer): string {
  return customer.subStatus || customer.status;
}

/** Reseller-side customer detail with header card and 5 tabs. */
type Customer = ResellerCustomer | typeof FALLBACK_CUSTOMER;

export function CustomerDetail() {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { data: resellerCustomers = [] } = useResellerCustomers();
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);
  // NO `?? resellerCustomers[0]` fallback. On an unmatched id this rendered a
  // DIFFERENT customer's page — with the "Record payment" modal bound to it, so cash
  // could be recorded against the wrong business. Fall through to the placeholder only,
  // which carries id 'fallback' and is already excluded from every write below.
  const customer: Customer =
    resellerCustomers.find((c) => c.id === id) ??
    FALLBACK_CUSTOMER;
  const notFound = !resellerCustomers.some((c) => c.id === id) && resellerCustomers.length > 0;
  const realId = customer.id !== 'fallback' ? customer.id : '';
  const { data: ledger } = useResellerCustomerLedger(realId);
  const { data: domains } = useCustomerDomains(realId);
  const { data: audit } = useCustomerAudit(realId);
  const sb = statusBadge(subStatusOf(customer));

  // Loaded, and this id is not one of yours: say so. Previously this rendered another
  // customer's page with the cash-payment modal live against them.
  if (notFound) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-16 max-w-[1200px] mx-auto text-center">
        <h1 className="text-xl font-semibold text-zinc-950 dark:text-zinc-50">Customer not found</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-300">
          This customer may have been removed, or the link is out of date.
        </p>
        <Link to="/reseller/customers" className="mt-6 inline-block text-sm font-medium text-brand dark:text-brand-dm hover:underline">
          ← Back to all customers
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1200px] mx-auto">
      <Link
        to="/reseller/customers"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-600 dark:text-zinc-300 hover:text-zinc-950 dark:hover:text-zinc-50 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        All customers
      </Link>

      {/* Header card */}
      <Card className="p-5 sm:p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0">
            <Avatar name={customer.name} size="xl" />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50">
                  {customer.name}
                </h1>
                <Badge tone={sb.tone}>{sb.label}</Badge>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">·</span>
                <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{customer.plan}</span>
              </div>
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
                Owner <span className="font-medium text-zinc-700 dark:text-zinc-200">{customer.ownerEmail}</span>{' '}
                · joined <span className="tabular-nums">{formatDate(customer.joinedAt)}</span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" iconLeft={<Receipt className="w-4 h-4" />} onClick={() => setPayOpen(true)}>
              Record cash payment
            </Button>
          </div>
        </div>
      </Card>

      {/* Past-due banner — only when the customer is actually past due. Dunning is
          driven by the automatic billing cron; the reseller records a payment here. */}
      {customer.isPastDue && (
        <Banner
          tone="warning"
          title="Payment past due"
          action={
            <Button size="sm" iconLeft={<Receipt className="w-4 h-4" />} onClick={() => setPayOpen(true)}>
              Record cash payment
            </Button>
          }
          className="mb-4"
        >
          The subscription auto-renewal hasn't been paid. Auto-suspend will trigger if payment isn't received.
          The system sends reminder emails automatically until the balance clears.
        </Banner>
      )}

      <CashPaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        businessId={customer.id}
        businessName={customer.name}
        onRecorded={() => {
          void qc.invalidateQueries({ queryKey: ['reseller', 'customers'] });
          void qc.invalidateQueries({ queryKey: ['reseller', 'ledger', customer.id] });
          void qc.invalidateQueries({ queryKey: ['reseller', 'payments'] });
        }}
      />

      <Tabs
        tabs={[
          { id: 'overview', label: 'Overview', content: <OverviewTab customer={customer} ledger={ledger} audit={audit} /> },
          { id: 'subscription', label: 'Subscription', content: <SubscriptionTab customer={customer} realId={realId} /> },
          { id: 'users', label: 'Users', content: <UsersTab businessId={customer.id} /> },
          { id: 'ledger', label: 'Ledger', count: ledger?.rows.length, content: <LedgerView ledger={ledger} /> },
          { id: 'extraction', label: 'Extraction', content: <ExtractionTab businessId={realId} businessName={customer.name} dbMode={customer.dbMode} /> },
          { id: 'domains', label: 'Domains', count: domains?.length, content: <DomainsTab domains={domains} /> },
          { id: 'audit', label: 'Audit', count: audit?.length, content: <AuditTab events={audit} /> },
        ]}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── */

type TimelineEntry = { id: string; kind: 'payment' | 'invoice' | 'audit' | 'join'; primary: string; date: string | null };

/** Build a real "recent timeline" from the customer's ledger (invoices+payments)
 *  and audit events. Newest first, capped. Empty when there's nothing real yet. */
function buildTimeline(ledger: BusinessLedger | undefined, audit: CustomerAuditEvent[] | undefined, joinedAt: string, plan: string): TimelineEntry[] {
  const entries: TimelineEntry[] = [];
  for (const [i, r] of (ledger?.rows ?? []).entries()) {
    if (r.kind === 'payment') {
      entries.push({ id: `pay-${r.ref}-${i}`, kind: 'payment', primary: `Payment ${moneyBdt(r.creditMinor)}${r.method ? ` (${r.method})` : ''}`, date: r.date });
    } else {
      entries.push({ id: `inv-${r.ref}-${i}`, kind: 'invoice', primary: r.description || `Invoice ${r.ref}`, date: r.date });
    }
  }
  for (const e of audit ?? []) {
    entries.push({ id: `aud-${e.id}`, kind: 'audit', primary: auditWhat(e.action), date: e.occurredAt });
  }
  entries.push({ id: 'join', kind: 'join', primary: `Joined on ${plan}`, date: joinedAt });
  return entries
    .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
    .slice(0, 8);
}
function moneyBdt(minor: number): string {
  return `৳${Math.round(minor / 100).toLocaleString('en-US')}`;
}
function timelineIcon(kind: TimelineEntry['kind']): React.ReactNode {
  if (kind === 'payment') return <CheckCircle className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />;
  if (kind === 'invoice') return <Receipt className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />;
  if (kind === 'join') return <History className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />;
  return <RefreshCcw className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />;
}

function OverviewTab({ customer, ledger, audit }: { customer: Customer; ledger?: BusinessLedger; audit?: CustomerAuditEvent[] }) {
  const sb = statusBadge(subStatusOf(customer));
  const timeline = buildTimeline(ledger, audit, customer.joinedAt, customer.plan);
  return (
    <div className="grid lg:grid-cols-3 gap-3 sm:gap-4">
      <Card className="lg:col-span-2 p-5 sm:p-6">
        <CardHeader title="Status" />
        <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6">
          <Field label="Subscription">
            <Badge tone={sb.tone}>{sb.label}</Badge>
          </Field>
          <Field label="Price">
            <Money minor={customer.unitAmountMinor ?? customer.mrrMinor} hideCents tone="neutral" className="font-medium text-zinc-950 dark:text-zinc-50" />
            <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-1">/ {intervalLabel(customer.interval)}</span>
          </Field>
          <Field label="Plan">{customer.plan}</Field>
          <Field label="Next renewal">
            <span className="tabular-nums">{customer.currentPeriodEnd ? formatDate(customer.currentPeriodEnd) : '—'}</span>
          </Field>
          <Field label="Database">
            <span className="inline-flex items-center gap-1.5">
              <Database className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />
              {dbModeLabel(customer.dbMode)}
            </span>
          </Field>
          <Field label="Owner contact">{customer.ownerEmail}</Field>
        </dl>
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Recent timeline" />
        {timeline.length === 0 ? (
          <p className="text-sm text-zinc-500 dark:text-zinc-400">No activity recorded yet.</p>
        ) : (
          <ul className="space-y-3 text-sm">
            {timeline.map((t) => (
              <TimelineItem
                key={t.id}
                icon={timelineIcon(t.kind)}
                primary={t.primary}
                date={t.date ? formatDate(t.date, { compact: true }) : '—'}
              />
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

function SubscriptionTab({ customer, realId }: { customer: Customer; realId: string }) {
  const sb = statusBadge(subStatusOf(customer));
  // Show the REAL per-period price + interval (e.g. "৳3,000 / year"), not the
  // monthly-normalized MRR. Fall back to MRR/month only when there's no sub row.
  const priceMinor = customer.unitAmountMinor ?? customer.mrrMinor;
  // A past-due sub's period end is already in the past, so "Renews" would misread as
  // upcoming — label it "Payment due" instead.
  const renewalLabel = customer.isPastDue ? 'Payment due' : 'Renews';
  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-6">
        <CardHeader
          title={
            <span className="flex items-center gap-2 flex-wrap">
              {customer.plan} ·{' '}
              <Money minor={priceMinor} hideCents tone="neutral" className="font-semibold" />
              <span className="text-sm font-normal text-zinc-500 dark:text-zinc-400">/ {intervalLabel(customer.interval)}</span>
            </span>
          }
          subtitle={
            <span className="tabular-nums">
              Started {formatDate(customer.joinedAt, { compact: true })}
              {customer.currentPeriodEnd ? <> · {renewalLabel} {formatDate(customer.currentPeriodEnd, { compact: true })}</> : null}
            </span>
          }
          action={<Badge tone={sb.tone}>{sb.label}</Badge>}
        />
        <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-2">
          Plan changes are managed by the business owner from their account, or by platform admins.
          Record an off-gateway payment from the header above.
        </p>

        {/* Cancellation used to say "ask the owner or a platform admin" — a reseller
            had to escalate to ITCity for something inside their own book of business.
            Cancel-at-period-end, the same mechanism the owner's control uses: the
            customer keeps the access they have already paid for (#5). */}
        {realId && subStatusOf(customer) !== 'canceled' && (
          <CancelSubscriptionControl
            businessId={realId}
            businessName={customer.name}
            currentPeriodEnd={customer.currentPeriodEnd ?? null}
            cancelAtPeriodEnd={customer.cancelAtPeriodEnd === true}
          />
        )}
      </Card>

      <Card className="p-5 sm:p-6">
        <CardHeader title="Add-ons" subtitle="None active" />
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          Customer hasn't enabled any paid add-ons yet.
        </p>
      </Card>
    </div>
  );
}

/** Users tab — this customer's members with live POS roles + edit/remove (Feature #37 + follow-up). */
function UsersTab({ businessId }: { businessId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="px-0 py-5 sm:py-6 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 sm:px-6 pb-3 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Users</h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
            Owners and staff with POS access. POS roles are read live from this customer's POS.
          </p>
        </div>
        <Button iconLeft={<UserPlus className="w-4 h-4" />} onClick={() => setOpen(true)}>
          Add user
        </Button>
      </div>
      <BusinessUsersTable businessId={businessId} />
      <AddCustomerUserModal open={open} onClose={() => setOpen(false)} businessId={businessId} />
    </Card>
  );
}

// (member table + edit/remove live in components/BusinessUsersTable — shared with the admin page)

/** Add a user to one of the reseller's customer businesses — mirrors the owner Team modal. */
function AddCustomerUserModal({
  open,
  onClose,
  businessId,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
}) {
  const [action, setAction] = useState<'create' | 'invite'>('create');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [posRole, setPosRole] = useState<string>('Cashier');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResellerInviteResult | ResellerCreateUserResult | null>(null);

  function close() {
    setAction('create');
    setEmail('');
    setName('');
    setUsername('');
    setPosRole('Cashier');
    setPassword('');
    setBusy(false);
    setError(null);
    setResult(null);
    onClose();
  }

  async function submit() {
    if (!email.trim() || !businessId) {
      setError('A valid email is required.');
      return;
    }
    if (action === 'create' && password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (action === 'create' && username.trim() && !/^[a-z0-9][a-z0-9._-]{2,39}$/i.test(username.trim())) {
      setError('Username must be 3–40 characters: letters, digits, dot, dash or underscore.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await createResellerCustomerUser(businessId, {
        action,
        email: email.trim(),
        name: name.trim() || undefined,
        posRole,
        password: action === 'create' ? password : undefined,
        username: action === 'create' ? username.trim() || undefined : undefined,
      });
      setResult(res);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      const message = e instanceof Error && e.message ? e.message : null;
      setError(
        status === 409
          ? (message ?? 'That person already has access to this business.')
          : status === 403
            ? 'You do not have permission to add users here.'
            : message ?? (action === 'create'
              ? 'Could not create the account.'
              : 'Could not send the invite.'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? ('inviteUrl' in result ? 'Invitation sent' : 'Account created') : 'Add a user'}
      size="md"
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!email.trim() || busy} loading={busy}>
              {action === 'create' ? 'Create account' : 'Send invite'}
            </Button>
          </>
        )
      }
    >
      {result ? (
        'inviteUrl' in result ? (
          <InviteLinkResult email={result.email} inviteUrl={result.inviteUrl} />
        ) : (
          <AccountCreatedPanel email={result.email} />
        )
      ) : (
        <div className="space-y-4">
          <CreateOrInviteToggle action={action} onChange={setAction} />
          <Input
            label="Email"
            required
            type="email"
            placeholder="owner@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Full name"
            placeholder="(optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {action === 'create' && (
            <>
              <Input
                label="Username"
                placeholder="(optional — auto-generated if empty)"
                hint="Their POS + portal login username."
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <Input
                label="Password"
                required
                type="password"
                hint="Minimum 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          )}
          <PosRoleSelector businessId={businessId} value={posRole} onChange={setPosRole} />
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      )}
    </Modal>
  );
}

/* ── Extraction (move this customer off shared/legacy MySQL onto its own dedicated
 *    database). Request-only from here — actual execution (preflight → freeze → copy
 *    → verify → cutover) is an operator-CLI action; see extraction-api.ts's module
 *    docstring for why a compromised reseller token must not be able to trigger a live
 *    freeze by itself. ─────────────────────────────────────────────────────────── */
const EXTRACTABLE_DB_MODES: string[] = ['legacy', 'shared'];
const EXTRACTION_HAPPY_PATH: ExtractionState[] = ['requested', 'preflight', 'provisioning', 'frozen', 'copying', 'verifying', 'cutover', 'done'];
const EXTRACTION_STATE_LABEL: Record<ExtractionState, string> = {
  requested: 'Requested', preflight: 'Preflight', provisioning: 'Provisioning', frozen: 'Frozen (read-only)',
  copying: 'Copying data', verifying: 'Verifying', cutover: 'Cutover', done: 'Done',
  aborted: 'Aborted', rolled_back: 'Rolled back', declined: 'Declined',
};
const EXTRACTION_STATE_TONE: Record<ExtractionState, 'success' | 'warning' | 'error' | 'info' | 'neutral'> = {
  requested: 'info', preflight: 'info', provisioning: 'info', frozen: 'warning', copying: 'info',
  verifying: 'info', cutover: 'warning', done: 'success', aborted: 'error', rolled_back: 'error',
  declined: 'neutral',
};

/** Ordered 10-state stepper (spec §6 phases) — mirrors admin/BusinessDetail.tsx's timeline
 *  exactly. `aborted`/`rolled_back`/`declined` fall off the happy path so they render as a flag
 *  instead of a step position, which would misleadingly imply "further along". */
function ExtractionPhaseTimeline({ state }: { state: ExtractionState }) {
  if (state === 'aborted' || state === 'rolled_back' || state === 'declined') {
    return (
      <div className="flex items-center gap-2 text-sm">
        <XCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 flex-shrink-0" />
        <span className="text-zinc-700 dark:text-zinc-200">
          {state === 'aborted'
            ? 'Aborted before cutover — the source database was never touched.'
            : state === 'declined'
              ? 'Declined by the platform — nothing was moved.'
              : 'Rolled back — reversed after cutover, back on the original database.'}
        </span>
      </div>
    );
  }
  const idx = EXTRACTION_HAPPY_PATH.indexOf(state);
  return (
    <ol className="flex flex-wrap items-center gap-y-2">
      {EXTRACTION_HAPPY_PATH.map((s, i) => (
        <li key={s} className="flex items-center">
          <span
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border whitespace-nowrap',
              i === idx
                ? 'border-brand bg-brand-subtle text-brand dark:border-brand-dm dark:bg-brand-dm-subtle/30 dark:text-brand-dm'
                : i < idx
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                  : 'border-zinc-200 bg-zinc-50 text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-500',
            )}
          >
            {i < idx && <CheckCircle className="w-3 h-3" />}
            {EXTRACTION_STATE_LABEL[s]}
          </span>
          {i < EXTRACTION_HAPPY_PATH.length - 1 && (
            <span className="w-3 sm:w-4 h-px bg-zinc-200 dark:bg-zinc-700 mx-1 flex-shrink-0" />
          )}
        </li>
      ))}
    </ol>
  );
}

/** cutoverAt + 30 days, ISO — the informational grace-window date (display only). */
function extractionGraceEndsAt(cutoverAt: string): string | null {
  const d = new Date(cutoverAt);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + 30);
  return d.toISOString();
}

function ExtractionTab({ businessId, businessName, dbMode }: { businessId: string; businessName: string; dbMode?: string }) {
  const { data, isLoading, isError, refetch } = useCustomerExtraction(businessId);
  const [requestOpen, setRequestOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true">
        <div className="skeleton h-6 w-40 rounded" />
        <div className="skeleton h-24 rounded-lg" />
      </div>
    );
  }

  // A failed fetch must NOT fall through to "no extraction requested yet" — this tab
  // gates a destructive freeze, and an operator misreading "nothing pending" while the
  // status call actually failed is exactly the wrong kind of silent failure here.
  if (isError) {
    return (
      <Banner tone="error" title="Couldn't load extraction status" action={<Button variant="secondary" size="sm" onClick={() => void refetch()}>Retry</Button>}>
        The request to the API failed — this is NOT the same as "no extraction requested". Retry, or check the API.
      </Banner>
    );
  }

  // `current` is the requested-or-active row (per the GET contract); once an attempt
  // reaches a terminal state (done/aborted/rolled_back) it drops out of `current`, so
  // fall back to the newest history row to keep showing what actually happened.
  const latest = data?.current ?? data?.history[0] ?? null;
  const extractable = EXTRACTABLE_DB_MODES.includes(dbMode ?? '');
  const graceEndsAt = latest && latest.state === 'done' && latest.cutoverAt ? extractionGraceEndsAt(latest.cutoverAt) : null;

  return (
    <div className="space-y-4">
      <Card className="p-5 sm:p-6">
        <CardHeader
          title="Move to a dedicated database"
          subtitle="Copies this customer's data out of the shared/legacy database into one it alone owns. A short off-hours freeze happens during the move; the POS shows a maintenance page for that window."
          action={
            !data?.current ? (
              <Button
                size="sm"
                iconLeft={<Database className="w-4 h-4" />}
                onClick={() => setRequestOpen(true)}
                disabled={!extractable || !businessId}
                title={!extractable ? 'Only legacy or shared businesses can be extracted to a dedicated database.' : undefined}
              >
                Request extraction
              </Button>
            ) : undefined
          }
        />
        {!latest ? (
          <p className="text-sm text-zinc-600 dark:text-zinc-300">No extraction has been requested for this customer yet.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge tone={EXTRACTION_STATE_TONE[latest.state]}>{EXTRACTION_STATE_LABEL[latest.state]}</Badge>
              <span className="text-xs text-zinc-500 dark:text-zinc-400 mono">{latest.publicId}</span>
            </div>
            {/* Approve/decline is platform-only (product decision) — this surface shows
                status text only, never a control. */}
            {data?.current && (
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                {!data.current.approvedAt
                  ? 'Awaiting platform approval — it will not run until the platform approves it and picks a night.'
                  : dateStatus(data.current.scheduledFor) === 'past'
                  ? // A stored date read back verbatim once told a reseller their customer's move
                    // was "scheduled for 08 Aug" on the 10th. Say what is true instead: it is late,
                    // it is still queued, and nothing has been done to the business.
                    `Was scheduled for ${slotOn(data.current.scheduledFor!)} and has not run yet — still queued for the next nightly run, tonight ${slotOn(dhakaToday())}. Several extractions can run in the same night, back-to-back, oldest request first; if one fails, the rest of that night is stood down deliberately. Nothing has been done to this business in the meantime.`
                  : dateStatus(data.current.scheduledFor) === 'today'
                  ? `Runs tonight, ${slotOn(data.current.scheduledFor!)}. Several extractions can run in the same night, back-to-back, never two at once — oldest request first, so this may begin somewhat later if others are ahead of it.`
                  : `Runs on the night of ${slotOn(data.current.scheduledFor!)}. Several extractions can run in the same night, back-to-back, never two at once — oldest request first, so this may begin somewhat later if others are ahead of it.`}
              </p>
            )}
            <ExtractionPhaseTimeline state={latest.state} />
            <dl className="grid sm:grid-cols-3 gap-y-2.5 gap-x-6 text-sm pt-2">
              <Field label="Requested">{latest.requestedAt ? formatDate(latest.requestedAt) : '—'}</Field>
              <Field label="Frozen">{latest.frozenAt ? formatDate(latest.frozenAt) : '—'}</Field>
              <Field label="Cutover">{latest.cutoverAt ? formatDate(latest.cutoverAt) : '—'}</Field>
            </dl>
            {latest.error && <p className="text-sm text-rose-600 dark:text-rose-400">{latest.error}</p>}
          </div>
        )}
      </Card>

      {graceEndsAt && latest?.cutoverAt && (
        <Banner tone="info" title="30-day rollback window">
          This customer finished moving to its dedicated database on {formatDate(latest.cutoverAt)}. The old
          database is kept until <strong>{formatDate(graceEndsAt)}</strong>. Rollback after this date is a
          data-loss decision, and nothing is ever deleted automatically.
        </Banner>
      )}

      {data && data.history.length > 1 && (
        <Card className="p-5 sm:p-6">
          <CardHeader title="Extraction history" subtitle="Every attempt for this customer, newest first." />
          <div className="overflow-x-auto nice-scroll -mx-5 sm:-mx-6">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                <tr>
                  <th className="text-left font-medium px-5 sm:px-6 py-2">State</th>
                  <th className="text-left font-medium px-4 py-2">Requested</th>
                  <th className="text-left font-medium px-4 py-2">Frozen</th>
                  <th className="text-left font-medium px-4 py-2">Cutover</th>
                  <th className="text-left font-medium px-5 sm:px-6 py-2">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                {data.history.map((h) => (
                  <tr key={h.publicId}>
                    <td className="px-5 sm:px-6 py-2.5"><Badge tone={EXTRACTION_STATE_TONE[h.state]}>{EXTRACTION_STATE_LABEL[h.state]}</Badge></td>
                    <td className="px-4 py-2.5 text-zinc-600 dark:text-zinc-300 tabular-nums">{h.requestedAt ? formatDate(h.requestedAt) : '—'}</td>
                    <td className="px-4 py-2.5 text-zinc-600 dark:text-zinc-300 tabular-nums">{h.frozenAt ? formatDate(h.frozenAt) : '—'}</td>
                    <td className="px-4 py-2.5 text-zinc-600 dark:text-zinc-300 tabular-nums">{h.cutoverAt ? formatDate(h.cutoverAt) : '—'}</td>
                    <td className="px-5 sm:px-6 py-2.5 text-rose-600 dark:text-rose-400 text-xs">{h.error ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ExtractionRequestModal open={requestOpen} onClose={() => setRequestOpen(false)} businessId={businessId} businessName={businessName} />
    </div>
  );
}

/**
 * Typed-confirmation request modal — same pattern as CancelSubscriptionControl below
 * (destructive button disabled until the input equals the required word). There is
 * deliberately no execute button anywhere in this modal: requesting only creates the
 * 'requested' row, and running the move (preflight → … → cutover) stays an operator-CLI
 * action. On a 403 (this reseller isn't sold 'managed' db mode), the server's own
 * message is shown as-is rather than pre-computed client-side.
 */
function ExtractionRequestModal({ open, onClose, businessId, businessName }: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
}) {
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    if (busy) return;
    setConfirmText('');
    setError(null);
    onClose();
  }
  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await requestCustomerExtraction(businessId, { acknowledgement: confirmText });
      void qc.invalidateQueries({ queryKey: ['reseller', 'customer', businessId, 'extraction'] });
      setConfirmText('');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not request the extraction. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title="Request extraction to a dedicated database?"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={submit} disabled={confirmText !== 'EXTRACT' || busy} loading={busy}>
            Request extraction
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          This moves <strong className="text-zinc-900 dark:text-zinc-100">{businessName}</strong> off the shared/legacy
          database and onto one it alone owns. During the move:
        </p>
        <ul className="list-disc list-inside text-sm text-zinc-600 dark:text-zinc-300 space-y-1">
          <li>The business is frozen read-only for a short, off-hours window.</li>
          <li>Its POS shows a maintenance page for that window — no sales or edits go through.</li>
          <li>The old database is kept for a 30-day rollback window after cutover.</li>
          <li>Nothing is ever deleted automatically — rollback stays a deliberate, manual action.</li>
        </ul>
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          This only records the request. Running the move itself is a separate operator action — there is no
          "execute" button here or anywhere in the portal.
        </p>
        <Input label="Type EXTRACT to confirm" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="EXTRACT" />
        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}

/** DNS/TLS status → badge tone + label (mirrors the domains enum; falls back). */
const DNS_BADGE: Record<string, { tone: 'success' | 'warning' | 'error'; label: string }> = {
  verified: { tone: 'success', label: 'Verified' },
  pending: { tone: 'warning', label: 'Pending' },
  failed: { tone: 'error', label: 'Failed' },
};
const TLS_BADGE: Record<string, { tone: 'success' | 'warning' | 'error'; label: string }> = {
  issued: { tone: 'success', label: 'Issued' },
  pending: { tone: 'warning', label: 'Pending' },
  failed: { tone: 'error', label: 'Failed' },
};

function DomainsTab({ domains }: { domains?: CustomerDomain[] }) {
  const rows = domains ?? [];
  if (rows.length === 0) {
    return (
      <EmptyTab
        icon={<Globe className="w-5 h-5" />}
        title="No custom domains"
        body="This customer uses the default platform subdomain. Custom domains added for their business will appear here."
      />
    );
  }
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto nice-scroll">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
            <tr>
              <th className="text-left font-medium px-5 sm:px-6 py-3">Hostname</th>
              <th className="text-left font-medium px-4 py-3">DNS</th>
              <th className="text-left font-medium px-4 py-3">TLS</th>
              <th className="text-left font-medium px-5 sm:px-6 py-3">Primary</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
            {rows.map((d) => {
              const dns = DNS_BADGE[d.dnsStatus] ?? { tone: 'warning' as const, label: d.dnsStatus };
              const tls = TLS_BADGE[d.tlsStatus] ?? { tone: 'warning' as const, label: d.tlsStatus };
              return (
                <tr key={d.id}>
                  <td className="px-5 sm:px-6 py-4">
                    <span className="inline-flex items-center gap-2 mono text-xs text-zinc-700 dark:text-zinc-200">
                      <Globe className="w-3.5 h-3.5 text-zinc-500 dark:text-zinc-400" />
                      {d.host}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <Badge tone={dns.tone}>{dns.label}</Badge>
                  </td>
                  <td className="px-4 py-4">
                    <Badge tone={tls.tone}>{tls.label}</Badge>
                  </td>
                  <td className="px-5 sm:px-6 py-4">
                    {d.isPrimary ? <Badge tone="info">Primary</Badge> : <span className="text-zinc-400 dark:text-zinc-500">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Audit display helpers — derive who/what/when defensively from the raw event. */
function auditWho(e: CustomerAuditEvent): string {
  const role = e.actorRole ? e.actorRole.split('_').pop() : null;
  if (e.actorName) return role ? `${e.actorName} (${role})` : e.actorName;
  if (e.actorEmail) return e.actorEmail;
  return 'system';
}
function auditWhat(action: string): string {
  const s = action.replace(/[._]/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Event';
}
function auditWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatDate(iso, { compact: true })} · ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

const AUDIT_DIFF_FIELD_LABEL: Record<string, string> = {
  package: 'Package',
  interval: 'Interval',
  priceMinor: 'Price',
};
function humanizeAuditKey(key: string): string {
  const s = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[._]/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : key;
}
function formatAuditDiffValue(field: string, v: unknown): string {
  if (field === 'priceMinor' && typeof v === 'number') return '৳' + (v / 100).toLocaleString('en-US');
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}
function isFromToShape(v: unknown): v is { from: unknown; to: unknown } {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && 'from' in v && 'to' in v;
}
function formatMetaValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function AuditTab({ events }: { events?: CustomerAuditEvent[] }) {
  const rows = events ?? [];
  if (rows.length === 0) {
    return (
      <EmptyTab
        icon={<History className="w-5 h-5" />}
        title="No audit events yet"
        body="Account and security events for this business will appear here as they happen."
      />
    );
  }
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
        {rows.map((e) => {
          const failed = e.outcome === 'failure' || e.severity === 'critical' || e.severity === 'warning';
          const diffEntries = e.diff ? Object.entries(e.diff) : [];
          const metaEntries = !e.diff && e.metadata ? Object.entries(e.metadata) : [];
          return (
            <li key={e.id} className="flex items-start gap-3 px-5 sm:px-6 py-3.5">
              <span className="w-7 h-7 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0">
                {failed ? <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" /> : <RefreshCcw className="w-3.5 h-3.5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-950 dark:text-zinc-50">{auditWhat(e.action)}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  <span>{auditWho(e)}</span>
                  <span className="mx-1.5">·</span>
                  <span className="tabular-nums">{auditWhen(e.occurredAt)}</span>
                </div>
                {diffEntries.length > 0 && (
                  <div className="mt-2 rounded-md border border-zinc-200 dark:border-zinc-700 overflow-hidden">
                    {diffEntries.map(([k, v], i) => (
                      <div
                        key={k}
                        className={
                          i > 0
                            ? 'grid grid-cols-[100px_1fr_auto_1fr] items-center gap-2 px-3 py-1.5 text-xs border-t border-zinc-200 dark:border-zinc-700'
                            : 'grid grid-cols-[100px_1fr_auto_1fr] items-center gap-2 px-3 py-1.5 text-xs'
                        }
                      >
                        <span className="font-medium text-zinc-600 dark:text-zinc-300">{AUDIT_DIFF_FIELD_LABEL[k] ?? humanizeAuditKey(k)}</span>
                        <span className="text-zinc-500 dark:text-zinc-400 text-right">{formatAuditDiffValue(k, v.from)}</span>
                        <span className="text-zinc-400">→</span>
                        <span className="text-zinc-900 dark:text-zinc-100 font-medium">{formatAuditDiffValue(k, v.to)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {metaEntries.length > 0 && (
                  <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400 space-y-0.5">
                    {metaEntries.map(([k, v]) => (
                      <div key={k}>
                        {humanizeAuditKey(k)}:{' '}
                        {isFromToShape(v) ? (
                          <span className="text-zinc-700 dark:text-zinc-200">{formatMetaValue(v.from)} → {formatMetaValue(v.to)}</span>
                        ) : (
                          <span className="text-zinc-700 dark:text-zinc-200">{formatMetaValue(v)}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** Shared honest empty state for the Domains / Audit tabs. */
function EmptyTab({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <Card className="p-10 text-center">
      <div className="w-11 h-11 mx-auto mb-3 grid place-items-center rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400">
        {icon}
      </div>
      <h3 className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">{title}</h3>
      <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400 max-w-sm mx-auto">{body}</p>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{label}</dt>
      <dd className="mt-0.5 text-sm text-zinc-800 dark:text-zinc-100 inline-flex items-center gap-1.5 flex-wrap">
        {children}
      </dd>
    </div>
  );
}

function TimelineItem({
  icon,
  primary,
  date,
}: {
  icon: React.ReactNode;
  primary: string;
  date: string;
}) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="mt-0.5 flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-zinc-900 dark:text-zinc-100">{primary}</div>
        <div className="text-xs text-zinc-500 dark:text-zinc-400 tabular-nums mt-0.5">{date}</div>
      </div>
    </li>
  );
}

// suppress unused
void Mail;

/**
 * Cancel / resume a customer's subscription (#5).
 *
 * Cancellation is SCHEDULED, never immediate: the flag is set, the customer keeps
 * the period they have already paid for, and the nightly billing cron ends the
 * subscription when that period lapses (and mirrors the status to the POS). So the
 * confirm copy states the actual end date rather than implying instant loss of
 * access, and while a cancellation is pending the only offered action is Resume.
 *
 * A typed confirmation is required, matching the owner-side dialog — this ends a
 * paying customer's service, and a reseller's customer list is a list of buttons
 * that all look the same.
 */
function CancelSubscriptionControl({
  businessId,
  businessName,
  currentPeriodEnd,
  cancelAtPeriodEnd,
}: {
  businessId: string;
  businessName: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const endLabel = currentPeriodEnd ? formatDate(currentPeriodEnd) : 'the end of the current period';

  async function run(resume: boolean) {
    setBusy(true);
    setError(null);
    try {
      await cancelCustomerSubscription(businessId, resume ? { resume: true } : { reason: reason.trim() || 'other' });
      await qc.invalidateQueries({ queryKey: ['reseller', 'customers'] });
      setOpen(false);
      setConfirmText('');
      setReason('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update the subscription. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (cancelAtPeriodEnd) {
    return (
      <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
        <Banner tone="warning" title="Cancellation scheduled">
          This subscription ends on <strong>{endLabel}</strong> and will not renew. The customer keeps
          full access until then.
        </Banner>
        {error && <p className="mt-2 text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        <div className="mt-3">
          <Button variant="secondary" size="sm" loading={busy} disabled={busy} onClick={() => void run(true)}>
            Resume subscription
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700">
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
        Cancel subscription
      </Button>

      <Modal
        open={open}
        onClose={() => { if (!busy) { setOpen(false); setError(null); } }}
        title="Cancel this subscription?"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>
              Keep it
            </Button>
            <Button
              variant="destructive"
              onClick={() => void run(false)}
              disabled={confirmText !== 'CANCEL' || busy}
              loading={busy}
            >
              Cancel subscription
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-zinc-600 dark:text-zinc-300">
            <strong className="text-zinc-900 dark:text-zinc-100">{businessName}</strong> keeps full access
            until <strong>{endLabel}</strong>, then the subscription ends and is not renewed. No further
            charges. You can undo this any time before that date.
          </p>
          <Input
            label="Reason (optional)"
            placeholder="e.g. closing the shop, switching provider"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Input
            label="Type CANCEL to confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="CANCEL"
          />
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
        </div>
      </Modal>
    </div>
  );
}
